/**
 * The single sanctioned localStorage boundary.
 *
 * Architecture rule (12_CUSTOMISABLE_INTERFACE_ARCHITECTURE_V5.md):
 * "Use a service/mock API, not browser localStorage as the source of truth.
 *  Local storage may hold an encrypted/non-sensitive recovery draft only."
 *
 * So this module stores layout/form drafts for offline recovery and nothing
 * else. It refuses to persist anything carrying a sensitive-looking key, and an
 * ESLint rule forbids `localStorage` everywhere except this file.
 */

const PREFIX = 'sme-mobile:draft:';

/** Keys that must never be written to disk, checked recursively before saving. */
const FORBIDDEN_KEY = /(pan|cvv|cvc|pin|secret|token|password|credential|iban|accountNumber|sortCode)/i;

export class SensitiveDraftError extends Error {
  readonly code = 'SENSITIVE_DRAFT_REJECTED';
  constructor(readonly field: string) {
    super(`Refusing to persist a recovery draft containing "${field}".`);
    this.name = 'SensitiveDraftError';
  }
}

function assertNonSensitive(value: unknown, path = ''): void {
  if (value === null || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((v, i) => assertNonSensitive(v, `${path}[${i}]`));
    return;
  }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (FORBIDDEN_KEY.test(k)) throw new SensitiveDraftError(path ? `${path}.${k}` : k);
    assertNonSensitive(v, path ? `${path}.${k}` : k);
  }
}

function available(): boolean {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
}

export type DraftEnvelope<T> = {
  readonly savedAt: string;
  readonly baseVersion: number;
  readonly baseEtag: string;
  readonly payload: T;
};

export function saveDraft<T>(key: string, envelope: DraftEnvelope<T>): void {
  assertNonSensitive(envelope.payload);
  if (!available()) return;
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(envelope));
  } catch {
    // Storage full or blocked (private mode). A lost draft is recoverable via
    // the service; it must never break the workflow.
  }
}

export function loadDraft<T>(key: string): DraftEnvelope<T> | null {
  if (!available()) return null;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw) as DraftEnvelope<T>;
  } catch {
    return null;
  }
}

export function clearDraft(key: string): void {
  if (!available()) return;
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    /* ignore */
  }
}

export function clearAllDrafts(): void {
  if (!available()) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
